import React, { useState, useEffect, Suspense } from 'react';
import { useAB } from '@guardian/ab-react';
import { tests } from '@frontend/web/experiments/ab-tests';
import { EditionDropdown } from '@frontend/web/components/EditionDropdown';
import { ShareCount } from '@frontend/web/components/ShareCount';
import { MostViewedFooter } from '@frontend/web/components/MostViewed/MostViewedFooter/MostViewedFooter';
import { RichLinkComponent } from '@frontend/web/components/elements/RichLinkComponent';
import { CalloutBlockComponent } from '@root/src/web/components/elements/CalloutBlockComponent';
import { YoutubeBlockComponent } from '@root/src/web/components/elements/YoutubeBlockComponent';
import { ReaderRevenueLinks } from '@frontend/web/components/ReaderRevenueLinks';
import { SlotBodyEnd } from '@frontend/web/components/SlotBodyEnd';
import { Links } from '@frontend/web/components/Links';
import { SubNav } from '@frontend/web/components/SubNav/SubNav';
import { GetMatchNav } from '@frontend/web/components/GetMatchNav';
import { Discussion } from '@frontend/web/components/Discussion';
import { StickyBottomBanner } from '@root/src/web/components/StickyBottomBanner/StickyBottomBanner';
import { SignInGateSelector } from '@root/src/web/components/SignInGate/SignInGateSelector';
import { incrementWeeklyArticleCount } from '@guardian/automat-client';
import { QandaAtom, GuideAtom, ProfileAtom, TimelineAtom, ChartAtom, } from '@guardian/atoms-rendering';
import { AudioAtomWrapper } from '@frontend/web/components/AudioAtomWrapper';
import { Portal } from '@frontend/web/components/Portal';
import { HydrateOnce } from '@frontend/web/components/HydrateOnce';
import { Lazy } from '@frontend/web/components/Lazy';
import { Placeholder } from '@root/src/web/components/Placeholder';
import { decidePillar } from '@root/src/web/lib/decidePillar';
import { decideDisplay } from '@root/src/web/lib/decideDisplay';
import { decideDesignType } from '@root/src/web/lib/decideDesignType';
import { loadScript } from '@root/src/web/lib/loadScript';
import { useOnce } from '@root/src/web/lib/useOnce';
import { initPerf } from '@root/src/web/browser/initPerf';
import { getCookie } from '@root/src/web/browser/cookie';
import { getCountryCode } from '@frontend/web/lib/getCountryCode';
import { getUser } from '@root/src/web/lib/getUser';
import { toTypesPillar } from '@root/src/lib/format';
import { FocusStyleManager } from '@guardian/src-foundations/utils';
import { incrementAlreadyVisited } from '@root/src/web/lib/alreadyVisited';
import { incrementDailyArticleCount } from '@frontend/web/lib/dailyArticleCount';
import { getArticleCountConsent } from '@frontend/web/lib/contributions';
import { buildAdTargeting } from '@root/src/lib/ad-targeting';
import { cmp, onConsentChange, getConsentFor, } from '@guardian/consent-management-platform';
import { injectPrivacySettingsLink } from '@root/src/web/lib/injectPrivacySettingsLink';
import { submitComponentEvent, } from '../browser/ophan/ophan';
import { trackPerformance } from '../browser/ga/ga';
// *******************************
// ****** Dynamic imports ********
// *******************************
const MostViewedRightWrapper = React.lazy(() => {
    const { start, end } = initPerf('MostViewedRightWrapper');
    start();
    return import(
    /* webpackChunkName: "MostViewedRightWrapper" */ '@frontend/web/components/MostViewed/MostViewedRight/MostViewedRightWrapper').then((module) => {
        end();
        return { default: module.MostViewedRightWrapper };
    });
});
const OnwardsUpper = React.lazy(() => {
    const { start, end } = initPerf('OnwardsUpper');
    start();
    return import(
    /* webpackChunkName: "OnwardsUpper" */ '@frontend/web/components/Onwards/OnwardsUpper').then((module) => {
        end();
        return { default: module.OnwardsUpper };
    });
});
const OnwardsLower = React.lazy(() => {
    const { start, end } = initPerf('OnwardsLower');
    start();
    return import(
    /* webpackChunkName: "OnwardsLower" */ '@frontend/web/components/Onwards/OnwardsLower').then((module) => {
        end();
        return { default: module.OnwardsLower };
    });
});
const GetMatchStats = React.lazy(() => {
    const { start, end } = initPerf('GetMatchStats');
    start();
    return import(
    /* webpackChunkName: "GetMatchStats" */ '@frontend/web/components/GetMatchStats').then((module) => {
        end();
        return { default: module.GetMatchStats };
    });
});
const componentEventHandler = (componentType, id, action) => () => {
    const componentEvent = {
        component: {
            componentType,
            id,
            products: [],
            labels: [],
        },
        action,
    };
    submitComponentEvent(componentEvent);
};
export const App = ({ CAPI, NAV }) => {
    const [isSignedIn, setIsSignedIn] = useState();
    const [user, setUser] = useState();
    const [countryCode, setCountryCode] = useState();
    // This is an async version of the countryCode state value defined above.
    // This can be used where you've got logic which depends on countryCode but
    // don't want to block on it becoming available, as you would with the
    // non-async version (this is the case in the banner picker where some
    // banners need countryCode but we don't want to block all banners from
    // executing their canShow logic until countryCode is available):
    const [asyncCountryCode, setAsyncCountryCode] = useState();
    const pageViewId = window.guardian?.config?.ophan?.pageViewId;
    // *******************************
    // ** Setup AB Test Tracking *****
    // *******************************
    const ABTestAPI = useAB();
    useEffect(() => {
        const allRunnableTests = ABTestAPI.allRunnableTests(tests);
        ABTestAPI.trackABTests(allRunnableTests);
        ABTestAPI.registerImpressionEvents(allRunnableTests);
        ABTestAPI.registerCompleteEvents(allRunnableTests);
    }, [ABTestAPI]);
    useEffect(() => {
        setIsSignedIn(!!getCookie('GU_U'));
    }, []);
    useOnce(() => {
        // useOnce means this code will only run once isSignedIn is defined, and only
        // run one time
        if (isSignedIn) {
            getUser(CAPI.config.discussionApiUrl).then((theUser) => {
                setUser(theUser);
            });
        }
        else {
            setUser(null);
        }
    }, [isSignedIn, CAPI.config.discussionApiUrl]);
    useEffect(() => {
        const callFetch = () => {
            const countryCodePromise = getCountryCode();
            setAsyncCountryCode(countryCodePromise);
            countryCodePromise.then((cc) => setCountryCode(cc || ''));
        };
        callFetch();
    }, []);
    useEffect(() => {
        incrementAlreadyVisited();
    }, []);
    // Log an article view using the Slot Machine client lib
    // This function must be called once per article serving.
    // We should monitor this function call to ensure it only happens within an
    // article pages when other pages are supported by DCR.
    useEffect(() => {
        const incrementArticleCountsIfConsented = async () => {
            if (await getArticleCountConsent()) {
                incrementDailyArticleCount();
                incrementWeeklyArticleCount();
            }
        };
        incrementArticleCountsIfConsented();
    }, []);
    // Ensure the focus state of any buttons/inputs in any of the Source
    // components are only applied when navigating via keyboard.
    // READ: https://www.theguardian.design/2a1e5182b/p/6691bb-accessibility/t/32e9fb
    useEffect(() => {
        FocusStyleManager.onlyShowFocusOnTabs();
    }, []);
    useEffect(() => {
        // Used internally only, so only import each function on demand
        const loadAndRun = (key) => (asExistingSupporter) => import(
        /* webpackChunkName: "readerRevenueDevUtils" */ '@frontend/web/lib/readerRevenueDevUtils')
            .then((utils) => utils[key](asExistingSupporter, CAPI.shouldHideReaderRevenue))
            /* eslint-disable no-console */
            .catch((error) => console.log('Error loading readerRevenueDevUtils', error));
        /* eslint-enable no-console */
        if (window && window.guardian) {
            window.guardian.readerRevenue = {
                changeGeolocation: loadAndRun('changeGeolocation'),
                showMeTheEpic: loadAndRun('showMeTheEpic'),
                showMeTheBanner: loadAndRun('showMeTheBanner'),
                showNextVariant: loadAndRun('showNextVariant'),
                showPreviousVariant: loadAndRun('showPreviousVariant'),
            };
        }
    }, [CAPI.shouldHideReaderRevenue]);
    // kick off the CMP...
    useEffect(() => {
        // the UI is injected automatically into the page,
        // and is not a react component, so it's
        // handled in here.
        if (CAPI.config.switches.consentManagement && countryCode) {
            const pubData = {
                platform: 'next-gen',
                browserId: getCookie('bwid') || undefined,
                pageViewId,
            };
            injectPrivacySettingsLink(); // manually updates the footer DOM because it's not hydrated
            // keep this in sync with CONSENT_TIMING in static/src/javascripts/boot.js in frontend
            // mark: CONSENT_TIMING
            let recordedConsentTime = false;
            onConsentChange(() => {
                if (!recordedConsentTime) {
                    recordedConsentTime = true;
                    cmp.willShowPrivacyMessage().then((willShow) => {
                        trackPerformance('consent', 'acquired', willShow ? 'new' : 'existing');
                    });
                }
            });
            cmp.init({
                country: countryCode,
                pubData,
            });
        }
    }, [countryCode, CAPI.config.switches.consentManagement, pageViewId]);
    // ************************
    // *   Google Analytics   *
    // ************************
    useEffect(() => {
        onConsentChange((state) => {
            const consentGiven = getConsentFor('google-analytics', state);
            if (consentGiven) {
                loadScript('https://www.google-analytics.com/analytics.js');
                loadScript(window.guardian.gaPath);
            }
            else {
                window.ga = null;
            }
        });
    }, []);
    const display = decideDisplay(CAPI);
    const designType = decideDesignType(CAPI.designType);
    const pillar = decidePillar({
        pillar: CAPI.pillar,
        design: designType,
    });
    const adTargeting = buildAdTargeting(CAPI.config);
    return (
    // Do you need to HydrateOnce or do you want a Portal?
    //
    // HydrateOnce: If your component is server rendered and you're hydrating it with
    // more data or making it interactive on the client and you do not need to access
    // global application state.
    //
    // Portal: If your component is not server rendered but a pure client-side component
    // and/or you want to access global application state, you want to use a Portal.
    //
    // Note: Both require a 'root' element that needs to be server rendered.
    React.createElement(React.StrictMode, null,
        React.createElement(Portal, { root: "reader-revenue-links-header" },
            React.createElement(ReaderRevenueLinks, { urls: CAPI.nav.readerRevenueLinks.header, edition: CAPI.editionId, dataLinkNamePrefix: "nav2 : ", inHeader: true, pageViewId: pageViewId })),
        React.createElement(HydrateOnce, { root: "links-root", waitFor: [user] },
            React.createElement(Links, { giftingURL: CAPI.nav.readerRevenueLinks.header.gifting, userId: user ? user.userId : undefined, idUrl: CAPI.config.idUrl, mmaUrl: CAPI.config.mmaUrl })),
        React.createElement(HydrateOnce, { root: "edition-root" },
            React.createElement(EditionDropdown, { edition: CAPI.editionId, dataLinkName: "nav2 : topbar : edition-picker: toggle" })),
        React.createElement(Portal, { root: "share-count-root" },
            React.createElement(ShareCount, { ajaxUrl: CAPI.config.ajaxUrl, pageId: CAPI.pageId })),
        CAPI.youtubeMainMediaBlockElement.map((youtubeBlock, index) => (React.createElement(HydrateOnce, { key: index, root: "youtube-block-main-media", index: youtubeBlock.youtubeIndex },
            React.createElement(YoutubeBlockComponent, { display: display, designType: designType, pillar: pillar, hideCaption: false, 
                // eslint-disable-next-line jsx-a11y/aria-role
                role: "inline", adTargeting: adTargeting, isMainMedia: false, id: youtubeBlock.id, assetId: youtubeBlock.assetId, channelId: youtubeBlock.channelId, expired: youtubeBlock.expired, overrideImage: youtubeBlock.overrideImage, posterImage: youtubeBlock.posterImage, duration: youtubeBlock.duration, mediaTitle: youtubeBlock.mediaTitle, altText: youtubeBlock.altText, origin: CAPI.config.host })))),
        CAPI.youtubeBlockElement.map((youtubeBlock, index) => (React.createElement(HydrateOnce, { key: index, root: "youtube-block", index: youtubeBlock.youtubeIndex },
            React.createElement(YoutubeBlockComponent, { display: display, designType: designType, pillar: pillar, hideCaption: false, 
                // eslint-disable-next-line jsx-a11y/aria-role
                role: "inline", adTargeting: adTargeting, isMainMedia: false, id: youtubeBlock.id, assetId: youtubeBlock.assetId, channelId: youtubeBlock.channelId, expired: youtubeBlock.expired, overrideImage: youtubeBlock.overrideImage, posterImage: youtubeBlock.posterImage, duration: youtubeBlock.duration, mediaTitle: youtubeBlock.mediaTitle, altText: youtubeBlock.altText, origin: CAPI.config.host })))),
        NAV.subNavSections && (React.createElement(HydrateOnce, { root: "sub-nav-root" },
            React.createElement(React.Fragment, null,
                React.createElement(SubNav, { subNavSections: NAV.subNavSections, currentNavLink: NAV.currentNavLink, pillar: pillar })))),
        CAPI.matchUrl && (React.createElement(Portal, { root: "match-nav" },
            React.createElement(GetMatchNav, { matchUrl: CAPI.matchUrl }))),
        CAPI.richLinks.map((link, index) => (React.createElement(Portal, { key: index, root: "rich-link", richLinkIndex: link.richLinkIndex },
            React.createElement(RichLinkComponent, { element: link, pillar: pillar, ajaxEndpoint: CAPI.config.ajaxUrl, richLinkIndex: index })))),
        CAPI.callouts.map((callout) => (React.createElement(HydrateOnce, { root: "callout", index: callout.calloutIndex },
            React.createElement(CalloutBlockComponent, { callout: callout, pillar: pillar })))),
        CAPI.chartAtoms.map((chart) => (React.createElement(HydrateOnce, { root: "chart-atom", index: chart.chartIndex },
            React.createElement(ChartAtom, { id: chart.id, html: chart.html })))),
        CAPI.audioAtoms.map((audioAtom) => (React.createElement(HydrateOnce, { root: "audio-atom", index: audioAtom.audioIndex },
            React.createElement(AudioAtomWrapper, { id: audioAtom.id, trackUrl: audioAtom.trackUrl, kicker: audioAtom.kicker, title: audioAtom.title, pillar: pillar, contentIsNotSensitive: !CAPI.config.isSensitive, aCastisEnabled: CAPI.config.switches.acast, readerCanBeShownAds: !CAPI.isAdFreeUser })))),
        CAPI.qandaAtoms.map((qandaAtom) => (React.createElement(HydrateOnce, { root: "qanda-atom", index: qandaAtom.qandaIndex },
            React.createElement(QandaAtom, { id: qandaAtom.id, title: qandaAtom.title, html: qandaAtom.html, image: qandaAtom.img, credit: qandaAtom.credit, pillar: toTypesPillar(pillar), likeHandler: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('QANDA_ATOM', qandaAtom.id, 'EXPAND') })))),
        CAPI.guideAtoms.map((guideAtom) => (React.createElement(HydrateOnce, { root: "guide-atom", index: guideAtom.guideIndex },
            React.createElement(GuideAtom, { id: guideAtom.id, title: guideAtom.title, html: guideAtom.html, image: guideAtom.img, credit: guideAtom.credit, pillar: toTypesPillar(pillar), likeHandler: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('GUIDE_ATOM', guideAtom.id, 'EXPAND') })))),
        CAPI.profileAtoms.map((profileAtom) => (React.createElement(HydrateOnce, { root: "profile-atom", index: profileAtom.profileIndex },
            React.createElement(ProfileAtom, { id: profileAtom.id, title: profileAtom.title, html: profileAtom.html, image: profileAtom.img, credit: profileAtom.credit, pillar: toTypesPillar(pillar), likeHandler: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('PROFILE_ATOM', profileAtom.id, 'EXPAND') })))),
        CAPI.timelineAtoms.map((timelineAtom) => (React.createElement(HydrateOnce, { root: "timeline-atom", index: timelineAtom.timelineIndex },
            React.createElement(TimelineAtom, { id: timelineAtom.id, title: timelineAtom.title, events: timelineAtom.events, description: timelineAtom.description, pillar: toTypesPillar(pillar), likeHandler: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'LIKE'), dislikeHandler: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'DISLIKE'), expandCallback: componentEventHandler('TIMELINE_ATOM', timelineAtom.id, 'EXPAND') })))),
        React.createElement(Portal, { root: "most-viewed-right" },
            React.createElement(Lazy, { margin: 100 },
                React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                    React.createElement(MostViewedRightWrapper, { pillar: pillar })))),
        CAPI.matchUrl && (React.createElement(Portal, { root: "match-stats" },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(Suspense, { fallback: React.createElement(Placeholder, { height: 800 }) },
                    React.createElement(GetMatchStats, { matchUrl: CAPI.matchUrl }))))),
        React.createElement(Portal, { root: "slot-body-end" },
            React.createElement(SlotBodyEnd, { isSignedIn: isSignedIn, countryCode: countryCode, contentType: CAPI.contentType, sectionName: CAPI.sectionName, shouldHideReaderRevenue: CAPI.shouldHideReaderRevenue, isMinuteArticle: CAPI.pageType.isMinuteArticle, isPaidContent: CAPI.pageType.isPaidContent, isSensitive: CAPI.config.isSensitive, tags: CAPI.tags, contributionsServiceUrl: CAPI.contributionsServiceUrl })),
        React.createElement(Portal, { root: isSignedIn
                ? 'onwards-upper-whensignedin'
                : 'onwards-upper-whensignedout' },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                    React.createElement(OnwardsUpper, { ajaxUrl: CAPI.config.ajaxUrl, hasRelated: CAPI.hasRelated, hasStoryPackage: CAPI.hasStoryPackage, isAdFreeUser: CAPI.isAdFreeUser, pageId: CAPI.pageId, isPaidContent: CAPI.config.isPaidContent || false, showRelatedContent: CAPI.config.showRelatedContent, keywordIds: CAPI.config.keywordIds, contentType: CAPI.contentType, tags: CAPI.tags, edition: CAPI.editionId, pillar: pillar, designType: designType })))),
        React.createElement(Portal, { root: isSignedIn
                ? 'onwards-lower-whensignedin'
                : 'onwards-lower-whensignedout' },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(Suspense, { fallback: React.createElement(React.Fragment, null) },
                    React.createElement(OnwardsLower, { ajaxUrl: CAPI.config.ajaxUrl, hasStoryPackage: CAPI.hasStoryPackage, tags: CAPI.tags, pillar: pillar, designType: designType })))),
        React.createElement(Portal, { root: "sign-in-gate" },
            React.createElement(SignInGateSelector, { isSignedIn: isSignedIn, CAPI: CAPI })),
        React.createElement(HydrateOnce, { root: "comments", waitFor: [user] },
            React.createElement(Discussion, { discussionApiUrl: CAPI.config.discussionApiUrl, shortUrlId: CAPI.config.shortUrlId, isCommentable: CAPI.isCommentable, pillar: pillar, user: user || undefined, discussionD2Uid: CAPI.config.discussionD2Uid, discussionApiClientHeader: CAPI.config.discussionApiClientHeader, enableDiscussionSwitch: CAPI.config.enableDiscussionSwitch, isAdFreeUser: CAPI.isAdFreeUser, shouldHideAds: CAPI.shouldHideAds, beingHydrated: true, display: display })),
        React.createElement(Portal, { root: "most-viewed-footer" },
            React.createElement(MostViewedFooter, { pillar: pillar, sectionName: CAPI.sectionName, ajaxUrl: CAPI.config.ajaxUrl, display: display, design: designType })),
        React.createElement(Portal, { root: "reader-revenue-links-footer" },
            React.createElement(Lazy, { margin: 300 },
                React.createElement(ReaderRevenueLinks, { urls: CAPI.nav.readerRevenueLinks.footer, edition: CAPI.editionId, dataLinkNamePrefix: "footer : ", inHeader: false, pageViewId: pageViewId }))),
        React.createElement(Portal, { root: "bottom-banner" },
            React.createElement(StickyBottomBanner, { isSignedIn: isSignedIn, asyncCountryCode: asyncCountryCode, CAPI: CAPI, idApiUrl: CAPI.config.idApiUrl }))));
};
